import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import {
    Box, Chip, Grid, IconButton, Menu, MenuItem,
    Stack, Tooltip, Typography, ListItemIcon, ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import LoadingComponent from '../../../common/components/LoadingComponent';
import DeleteGroup from './DeleteGroup';

const BRAND = '#0F2FEB';

const tableStyles = {
    rows: {
        style: {
            minHeight: '3.8rem',
            '&:hover': { backgroundColor: '#F3F6FF !important' },
        },
    },
    headRow: {
        style: {
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            backgroundColor: '#F8F9FA',
            borderBottom: '2px solid #E0E0E0',
        },
    },
    pagination: { style: { backgroundColor: '#fff' } },
    expanderRow: { style: { backgroundColor: '#FAFBFF' } },
};

function RowActions({ row, onEditGroup }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const menuOpen = Boolean(anchorEl);
    const openMenu = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
    const closeMenu = () => setAnchorEl(null);
    const run = (fn) => { closeMenu(); fn(); };

    return (
        <>
            <Tooltip title="Actions" arrow>
                <IconButton size="small" onClick={openMenu} sx={{ color: 'text.secondary' }}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={closeMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ elevation: 3, sx: { minWidth: 180, borderRadius: 2, mt: 0.5 } }}
            >
                <MenuItem onClick={() => run(() => onEditGroup(row))}>
                    <ListItemIcon><EditRoundedIcon fontSize="small" sx={{ color: BRAND }} /></ListItemIcon>
                    <ListItemText>Edit Group</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => run(() => setDeleteOpen(true))} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteRoundedIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Delete Group</ListItemText>
                </MenuItem>
            </Menu>

            <DeleteGroup group={row} isOpen={deleteOpen} setsetIsOpen={setDeleteOpen} />
        </>
    );
}

function ExpandedRow({ data }) {
    return (
        <Box sx={{ px: 4, py: 2.5, bgcolor: '#FAFBFF', borderTop: '1px solid #E8EAF6' }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1, letterSpacing: '0.08em', fontWeight: 700 }}
                    >
                        Permissions ({data.permissions?.length || 0})
                    </Typography>
                    {data.permissions?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                            {data.permissions.map((p, i) => (
                                <Chip
                                    key={i} label={p} size="small" variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 22, color: '#6A1B9A', borderColor: '#CE93D8' }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.disabled" fontStyle="italic">
                            No permissions assigned
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1, letterSpacing: '0.08em', fontWeight: 700 }}
                    >
                        Members ({data.is_public ? 'All' : data.users?.length || 0})
                    </Typography>
                    {data.is_public ? (
                        <Chip label="All Users" size="small" color="primary" variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 22 }} />
                    ) : data.users?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                            {data.users.map((u, i) => (
                                <Chip key={i} label={u} size="small" variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 22 }} />
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.disabled" fontStyle="italic">
                            No members assigned
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

export default function GroupTable({ filteredGroups, isLoading, onEditGroup }) {
    const columns = [
        {
            name: 'GROUP NAME',
            selector: row => row.name,
            sortable: true,
            minWidth: '200px',
            cell: row => (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ p: 0.7, borderRadius: 1.5, bgcolor: '#EEF1FD', display: 'flex' }}>
                        <PeopleAltRoundedIcon sx={{ color: BRAND, fontSize: '1.1rem' }} />
                    </Box>
                    <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                </Stack>
            ),
        },
        {
            name: 'PERMISSIONS',
            selector: row => row.permissions?.length || 0,
            sortable: true,
            minWidth: '220px',
            cell: row => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, py: 0.5 }}>
                    {row.permissions?.length > 0 ? (
                        <>
                            {row.permissions.slice(0, 2).map((p, i) => (
                                <Chip
                                    key={i} label={p} size="small" variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: 20, color: '#6A1B9A', borderColor: '#CE93D8' }}
                                />
                            ))}
                            {row.permissions.length > 2 && (
                                <Tooltip title={row.permissions.slice(2).join(', ')} arrow>
                                    <Chip
                                        label={`+${row.permissions.length - 2}`} size="small"
                                        sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#F3E5F5', color: '#6A1B9A' }}
                                    />
                                </Tooltip>
                            )}
                        </>
                    ) : (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">None</Typography>
                    )}
                </Box>
            ),
        },
        {
            name: 'MEMBERS',
            selector: row => row.is_public ? Infinity : row.users?.length || 0,
            sortable: true,
            cell: row => (
                row.is_public ? (
                    <Chip label="All Users" size="small" color="primary"
                        sx={{ fontSize: '0.7rem', height: 22 }} />
                ) : (
                    <Chip
                        label={`${row.users?.length || 0} member${row.users?.length !== 1 ? 's' : ''}`}
                        size="small"
                        color={row.users?.length ? 'default' : 'warning'}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                )
            ),
        },
        {
            name: '',
            cell: row => <RowActions row={row} onEditGroup={onEditGroup} />,
            ignoreRowClick: true,
            button: true,
            width: '56px',
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={filteredGroups}
            pagination
            highlightOnHover
            customStyles={tableStyles}
            progressPending={isLoading}
            progressComponent={<LoadingComponent />}
            expandableRows
            expandOnRowClicked
            expandableRowsComponent={ExpandedRow}
            noDataComponent={
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">No groups found.</Typography>
                </Box>
            }
        />
    );
}